import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { RolePermissions } from "../../components/PermissionsContext";
import { PERMISSION_GROUPS, PermissionGroup } from "../../utils/permissionGroups";
import "./PermissionsEditor.css";

export type PermissionsEditorProps = {
  value: RolePermissions;
  onChange: (next: RolePermissions) => void;
  readOnly?: boolean;
};

function countSelected(group: PermissionGroup, value: RolePermissions): { sel: number; total: number } {
  let sel = 0;
  let total = 0;
  for (const r of group.resources) {
    total += r.actions.length;
    const list = value[r.key] || [];
    for (const a of r.actions) {
      if (list.includes(a.value) || list.includes("all")) sel++;
    }
  }
  return { sel, total };
}

export const PermissionsEditor: React.FC<PermissionsEditorProps> = ({ value, onChange, readOnly }) => {
  const [open, setOpen] = React.useState<Record<string, boolean>>({ org: true });

  function toggleAction(resource: keyof RolePermissions, action: string) {
    if (readOnly) return;
    const list = value[resource] || [];
    const next = list.includes(action) ? list.filter((a) => a !== action) : [...list, action];
    onChange({ ...value, [resource]: next });
  }

  function selectAllInGroup(group: PermissionGroup, on: boolean) {
    if (readOnly) return;
    const next = { ...value };
    for (const r of group.resources) {
      next[r.key] = on ? r.actions.map((a) => a.value) : [];
    }
    onChange(next);
  }

  return (
    <div className="permissionsEditor">
      {PERMISSION_GROUPS.map((group) => {
        const { sel, total } = countSelected(group, value);
        const isOpen = !!open[group.key];
        return (
          <div className="permGroup" key={group.key}>
            <button
              className="permGroupHeader"
              type="button"
              onClick={() => setOpen((s) => ({ ...s, [group.key]: !s[group.key] }))}
            >
              {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              <span className="permGroupLabel">{group.label}</span>
              <span className="permGroupCount">{sel}/{total}</span>
              {!readOnly && (
                <span
                  className="permGroupSelectAll"
                  onClick={(e) => { e.stopPropagation(); selectAllInGroup(group, sel < total); }}
                >
                  {sel < total ? "Selecionar tudo" : "Limpar"}
                </span>
              )}
            </button>
            {isOpen && (
              <div className="permGroupBody">
                {group.resources.map((res) => {
                  const list = value[res.key] || [];
                  return (
                    <div className="permResource" key={String(res.key)}>
                      <div className="permResourceLabel">{res.label}</div>
                      <div className="permResourceActions">
                        {res.actions.map((act) => {
                          const checked = list.includes(act.value) || list.includes("all");
                          return (
                            <label className="permAction" key={act.value}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={readOnly}
                                onChange={() => toggleAction(res.key, act.value)}
                              />
                              <span>{act.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
